import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Button,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  useTheme
} from '@mui/material';
import {
  People as Users,
  Business as Building,
  Event as Calendar,
  CheckBox as CheckSquare,
  TrendingUp,
  Phone,
  Home,
  CalendarMonth as CalendarDays,
  CheckCircle
} from '@mui/icons-material';
import { getUserInfo } from '../../service/Auth/Auth.js';
import { getCurrentConfig } from '../../config/config.js';

const Dashboard = () => {
  const [stats, setStats] = useState({
    contacts: 0,
    listings: 0,
    upcomingOFI: 0,
    pendingActions: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  
  const userInfo = getUserInfo();
  const config = getCurrentConfig();
  const theme = useTheme();

  useEffect(() => {
    // Simulate loading stats - in real app, make API calls
    const loadStats = async () => {
      try {
        // TODO: Replace with actual API calls
        await new Promise(resolve => setTimeout(resolve, 1000));
        setStats({
          contacts: 156,
          listings: 23,
          upcomingOFI: 8,
          pendingActions: 12
        });
      } catch (error) {
       
      } finally {
        setIsLoading(false);
      }
    };

    loadStats();
  }, []);

  const dashboardCards = [
    {
      title: 'Total Contacts',
      value: stats.contacts,
      icon: Users,
      color: '#007bff',
      link: '/contacts'
    },
    {
      title: 'Active Listings',
      value: stats.listings,
      icon: Building,
      color: '#28a745',
      link: '/listings'
    },
    {
      title: 'Upcoming OFI',
      value: stats.upcomingOFI,
      icon: Calendar,
      color: '#ffc107',
      link: '/ofi'
    },
    {
      title: 'Pending Actions',
      value: stats.pendingActions,
      icon: CheckSquare,
      color: '#dc3545',
      link: '/actions'
    }
  ];

  const recentActivities = [
    {
      icon: Phone,
      text: 'Called John Smith about 123 Main St',
      color: '#007bff'
    },
    {
      icon: Home,
      text: 'Added new listing: 456 Oak Avenue',
      color: '#28a745'
    },
    {
      icon: CalendarDays,
      text: 'Scheduled OFI for this weekend',
      color: '#ffc107'
    },
    {
      icon: CheckCircle,
      text: 'Completed follow-up with buyer leads',
      color: '#28a745'
    }
  ];

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="textSecondary">
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Welcome Header */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                width: 60,
                height: 60,
                bgcolor: config.primaryColor,
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}
            >
              {userInfo.firstName?.charAt(0)}{userInfo.lastName?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Welcome back, {userInfo.firstName}!
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Here's what's happening with your business today.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {dashboardCards.map((card, index) => {
          const IconComponent = card.icon;
          return (
            <Grid  md={3} key={index}>
              <Card
                component={Link}
                to={card.link}
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[4]
                  }
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 1,
                        backgroundColor: `${card.color}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <IconComponent sx={{ color: card.color }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h3"
                        component="h3"
                        sx={{
                          fontWeight: 'bold',
                          color: card.color,
                          mb: 0.5
                        }}
                      >
                        {card.value}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {card.title}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Quick Actions */}
      <Grid container spacing={3}>
        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <TrendingUp />
                <Typography variant="h6" component="h3">
                  Recent Activity
                </Typography>
              </Box>
              <List sx={{ p: 0 }}>
                {recentActivities.map((activity, index) => {
                  const IconComponent = activity.icon;
                  return (
                    <ListItem
                      key={index}
                      sx={{
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        mb: 1,
                        '&:last-child': { mb: 0 }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <IconComponent sx={{ color: activity.color }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={activity.text}
                        primaryTypographyProps={{
                          variant: 'body2'
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h3" gutterBottom>
                Quick Actions
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Button
                  component={Link}
                  to="/contacts/add"
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: config.primaryColor,
                    '&:hover': {
                      backgroundColor: config.primaryColor,
                      opacity: 0.9
                    }
                  }}
                >
                  Add New Contact
                </Button>
                <Button
                  component={Link}
                  to="/listings/add"
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: config.primaryColor,
                    '&:hover': {
                      backgroundColor: config.primaryColor,
                      opacity: 0.9
                    }
                  }}
                >
                  Create New Listing
                </Button>
                <Button
                  component={Link}
                  to="/ofi/add"
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: config.primaryColor,
                    '&:hover': {
                      backgroundColor: config.primaryColor,
                      opacity: 0.9
                    }
                  }}
                >
                  Schedule Open Home
                </Button>
                <Button
                  component={Link}
                  to="/actions/add"
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: config.primaryColor,
                    '&:hover': {
                      backgroundColor: config.primaryColor,
                      opacity: 0.9
                    }
                  }}
                >
                  Add Action Item
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;